import { useEffect, useState } from 'react';
import { useRequestsContext } from '@/common/hooks/requestHook';
import {
	getCallAnalysisConfig,
	getCallAnalysisPromptPreview,
	updateCallAnalysisConfig,
} from '@/API/repositories/callAnalysis';

const buildId = (label, existingIds, prefix) => {
	const slug = String(label || '')
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');

	let id = slug || `${prefix}_${Date.now()}`;
	if (existingIds.has(id)) {
		id = `${id}_${Date.now()}`;
	}
	return id;
};

const useCallAnalysisConfig = () => {
	const [config, setConfig] = useState(null);
	const [promptPreview, setPromptPreview] = useState('');
	const { makeRequest, hasUnfilledRequest } = useRequestsContext();

	const loadPromptPreview = async (nextConfig = config) => {
		if (!nextConfig) {
			return;
		}

		const response = await makeRequest(
			getCallAnalysisPromptPreview.bind(null, {
				sections: nextConfig.sections,
				critical_errors: nextConfig.critical_errors,
				zero_score_on_critical_error: nextConfig.zero_score_on_critical_error,
			}),
		);

		if (response?.data?.full_prompt) {
			setPromptPreview(response.data.full_prompt);
		}
	};

	const loadConfig = async () => {
		const response = await makeRequest(getCallAnalysisConfig);
		if (response?.data) {
			setConfig(response.data);
			await loadPromptPreview(response.data);
		}
	};

	const handleSave = async () => {
		if (!config?._id) {
			return;
		}

		const response = await makeRequest(
			updateCallAnalysisConfig.bind(null, config._id, {
				name: config.name,
				sections: config.sections,
				critical_errors: config.critical_errors,
				zero_score_on_critical_error: config.zero_score_on_critical_error,
				is_active: true,
			}),
		);

		if (response?.data) {
			setConfig(response.data);
			await loadPromptPreview(response.data);
		}
	};

	const updateSection = (sectionId, field, value) => {
		setConfig((prev) => ({
			...prev,
			sections: (prev.sections || []).map((item) =>
				item.section_id === sectionId
					? {
							...item,
							[field]: field === 'weight' ? Number(value) : value,
						}
					: item,
			),
		}));
	};

	const addSection = () => {
		setConfig((prev) => {
			const label = 'New section';
			const existingIds = new Set(
				(prev.sections || []).map((item) => item.section_id),
			);

			return {
				...prev,
				sections: [
					...(prev.sections || []),
					{
						section_id: buildId(label, existingIds, 'section'),
						label,
						description: '',
						weight: 0,
						criteria: [],
					},
				],
			};
		});
	};

	const removeSection = (sectionId) => {
		setConfig((prev) => ({
			...prev,
			sections: (prev.sections || []).filter(
				(item) => item.section_id !== sectionId,
			),
		}));
	};

	const updateCriterion = (sectionId, criterionId, field, value) => {
		setConfig((prev) => ({
			...prev,
			sections: (prev.sections || []).map((section) => {
				if (section.section_id !== sectionId) {
					return section;
				}
				return {
					...section,
					criteria: (section.criteria || []).map((item) =>
						item.criterion_id === criterionId
							? { ...item, [field]: value }
							: item,
					),
				};
			}),
		}));
	};

	const addCriterion = (sectionId) => {
		setConfig((prev) => {
			const label = 'New criterion';
			const existingIds = new Set(
				(prev.sections || []).flatMap((section) =>
					(section.criteria || []).map((item) => item.criterion_id),
				),
			);

			return {
				...prev,
				sections: (prev.sections || []).map((section) => {
					if (section.section_id !== sectionId) {
						return section;
					}
					return {
						...section,
						criteria: [
							...(section.criteria || []),
							{
								criterion_id: buildId(label, existingIds, 'criterion'),
								label,
								description: '',
								is_active: true,
							},
						],
					};
				}),
			};
		});
	};

	const removeCriterion = (sectionId, criterionId) => {
		setConfig((prev) => ({
			...prev,
			sections: (prev.sections || []).map((section) => {
				if (section.section_id !== sectionId) {
					return section;
				}
				return {
					...section,
					criteria: (section.criteria || []).filter(
						(item) => item.criterion_id !== criterionId,
					),
				};
			}),
		}));
	};

	const updateCriticalError = (errorId, field, value) => {
		setConfig((prev) => ({
			...prev,
			critical_errors: prev.critical_errors.map((item) =>
				item.error_id === errorId ? { ...item, [field]: value } : item,
			),
		}));
	};

	const addCriticalError = () => {
		const errorId = `custom_${Date.now()}`;
		setConfig((prev) => ({
			...prev,
			critical_errors: [
				...(prev.critical_errors || []),
				{
					error_id: errorId,
					label: 'New critical error',
					description: '',
					is_active: true,
				},
			],
		}));
	};

	const removeCriticalError = (errorId) => {
		setConfig((prev) => ({
			...prev,
			critical_errors: (prev.critical_errors || []).filter(
				(item) => item.error_id !== errorId,
			),
		}));
	};

	const updateConfigField = (field, value) => {
		setConfig((prev) => ({ ...prev, [field]: value }));
	};

	const isLoading = hasUnfilledRequest(
		getCallAnalysisConfig,
		updateCallAnalysisConfig,
		getCallAnalysisPromptPreview,
	);

	useEffect(() => {
		loadConfig();
	}, []);

	return {
		config,
		promptPreview,
		isLoading,
		handleSave,
		loadPromptPreview,
		updateSection,
		addSection,
		removeSection,
		updateCriterion,
		addCriterion,
		removeCriterion,
		updateCriticalError,
		addCriticalError,
		removeCriticalError,
		updateConfigField,
	};
};

export default useCallAnalysisConfig;
